import React, { useEffect, useRef } from 'react';
import { isProtectiveForestsCnl } from './utils';

const InfoSection = ({
  displayData,
  setLocalFormData,
  setFormData,
  handbooks,
  isFieldsLocked,
  openPicker,
  openYearPicker,
  clearTableData,
  infoTableScrollRef,
  infoTableScrollLeft,
  handleInfoTableScroll
}) => {
  // П. 2.8: «Категория защитных лесов» обязательна, только если «Целевое назначение
  // лесов» выбрано как «Защитные леса».
  const isKzlRequired = isProtectiveForestsCnl(displayData.cnl, handbooks);

  // Автоподгонка высоты textarea под полный текст (перенос строк вместо обрезки) —
  // как «Цель ВНН» на форме №1. Общая для всех справочных полей блока «Информация».
  const cnlTextareaRef = useRef(null);
  const kzlTextareaRef = useRef(null);
  const proishozdenieTextareaRef = useRef(null);
  const ozuTextareaRef = useRef(null);
  const arendaTextareaRef = useRef(null);
  const vidarendaTextareaRef = useRef(null);
  const ooptTextareaRef = useRef(null);
  const nooptTextareaRef = useRef(null);

  const adjustTextareaHeight = (textarea) => {
    if (textarea) {
      textarea.style.height = '0px';
      textarea.style.height = Math.max(110, textarea.scrollHeight) + 'px';
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      adjustTextareaHeight(cnlTextareaRef.current);
      adjustTextareaHeight(kzlTextareaRef.current);
      adjustTextareaHeight(proishozdenieTextareaRef.current);
      adjustTextareaHeight(ozuTextareaRef.current);
      adjustTextareaHeight(arendaTextareaRef.current);
      adjustTextareaHeight(vidarendaTextareaRef.current);
      adjustTextareaHeight(ooptTextareaRef.current);
      adjustTextareaHeight(nooptTextareaRef.current);
    }, 0);
    return () => clearTimeout(timer);
  }, [
    displayData.cnl,
    displayData.kzl,
    displayData.proishozdenie,
    displayData.ozu,
    displayData.arenda,
    displayData.vidarenda,
    displayData.oopt,
    displayData.noopt
  ]);

  // Общий стиль переноса строк и авторасширения для читаемых справочных полей блока.
  const wrappingTextareaStyle = {
    border: 'none',
    outline: 'none',
    resize: 'none',
    width: '100%',
    minHeight: '110px',
    height: 'auto',
    overflow: 'auto',
    background: 'transparent',
    lineHeight: '1.5',
    padding: '4px 8px',
    whiteSpace: 'normal',
    wordWrap: 'break-word',
    overflowWrap: 'break-word',
    wordBreak: 'break-word',
    display: 'block',
    textAlign: 'center',
    position: 'static',
    boxSizing: 'border-box'
  };
  const wrappingCellStyle = {
    minHeight: 110,
    height: 'auto',
    position: 'relative',
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'center'
  };

  return (
    <div className="group-section">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
        <h4>Информация</h4>
        <button 
          type="button" 
          onClick={() => clearTableData('info')}
          style={{
            background: '#6c757d',
            color: 'white',
            border: 'none',
            padding: '5px 10px',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '12px'
          }}
        >
          Очистить
        </button>
      </div>
      <div 
        ref={infoTableScrollRef}
        onScroll={handleInfoTableScroll}
        style={{ 
          overflowX: 'auto', 
          overflowY: 'visible',
          maxWidth: '100%',
          border: '1px solid #ddd',
          borderRadius: '6px'
        }}
      >
        <table className="table" style={{ 
          minWidth: '1350px',
          width: 'max-content',
          marginBottom: 0
        }}>
          <thead>
            <tr>
              <th style={{ minWidth: '220px', width: '220px' }}>Целевое назначение лесов <span className="required">*</span></th>
              <th style={{ minWidth: '320px', width: '320px' }}>Кат ЗЛ {isKzlRequired && <span className="required">*</span>}</th>
              <th style={{ minWidth: '260px', width: '260px' }}>Происхождение насаждения</th>
              <th style={{ minWidth: '280px', width: '280px' }}>ОЗУ</th>
              <th style={{ minWidth: '220px', width: '220px' }}>Право пользования</th>
              <th style={{ minWidth: '240px', width: '240px' }}>Вид использования</th>
              <th style={{ minWidth: '260px', width: '260px' }}>ООПТ</th>
              <th style={{ minWidth: '200px', width: '200px' }}>Иное ООПТ</th>
              <th style={{ minWidth: '120px', width: '120px' }}>Год лесоустройства <span className="required">*</span></th>
              <th style={{ minWidth: '150px', width: '150px' }}>Информация пользователя</th>
              <th style={{ minWidth: '150px', width: '150px' }}>Доступность участка <span className="required">*</span></th>
            </tr>
          </thead>
          <tbody>
            <tr>
              {/* Целевое назначение лесов */}
              <td className="coordinate-cell" style={{ minWidth: '220px', width: '220px' }}>
                <div
                  className={`reference-cell ${isFieldsLocked ? 'locked-field' : ''}`}
                  onClick={() => {
                    if (!isFieldsLocked) {
                      openPicker('cnl', 16);
                    }
                  }}
                  style={{
                    ...wrappingCellStyle,
                    cursor: isFieldsLocked ? 'not-allowed' : 'pointer',
                    background: isFieldsLocked ? '#f8f9fa' : 'transparent'
                  }}
                >
                  <textarea
                    ref={cnlTextareaRef}
                    readOnly
                    value={(() => {
                      const handbook = handbooks.find(h => h.id === 16);
                      if (handbook && handbook.references && displayData.cnl) {
                        const selectedOption = handbook.references.find(r => r.id.toString() === displayData.cnl);
                        return selectedOption ? selectedOption.name : '';
                      }
                      return '';
                    })()}
                    placeholder="☰"
                    style={{
                      ...wrappingTextareaStyle,
                      color: displayData.cnl ? '#495057' : '#6c757d',
                      fontStyle: displayData.cnl ? 'normal' : 'italic'
                    }}
                    className={`form-control multiline-textarea ${!displayData.cnl ? 'required-field' : ''}`}
                  />
                </div>
              </td>

              {/* Кат ЗЛ */}
              <td className="coordinate-cell" style={{ minWidth: '320px', width: '320px' }}>
                <div
                  className={`reference-cell ${isFieldsLocked ? 'locked-field' : ''}`}
                  onClick={() => {
                    if (!isFieldsLocked) {
                      openPicker('kzl', 5);
                    }
                  }}
                  style={{
                    ...wrappingCellStyle,
                    cursor: isFieldsLocked ? 'not-allowed' : 'pointer',
                    background: isFieldsLocked ? '#f8f9fa' : 'transparent'
                  }}
                >
                  <textarea
                    ref={kzlTextareaRef}
                    readOnly
                    value={(() => {
                      const handbook = handbooks.find(h => h.id === 5);
                      if (handbook && handbook.references && displayData.kzl) {
                        const selectedOption = handbook.references.find(r => r.id.toString() === displayData.kzl);
                        return selectedOption ? selectedOption.name : '';
                      }
                      return '';
                    })()}
                    placeholder="☰"
                    style={{
                      ...wrappingTextareaStyle,
                      color: displayData.kzl ? '#495057' : '#6c757d',
                      fontStyle: displayData.kzl ? 'normal' : 'italic'
                    }}
                    className={`form-control multiline-textarea ${(isKzlRequired && !displayData.kzl) ? 'required-field' : ''}`}
                  />
                </div>
              </td>

              {/* Происхождение насаждения */}
              <td className="coordinate-cell" style={{ minWidth: '260px', width: '260px' }}>
                <div
                  className={`reference-cell ${isFieldsLocked ? 'locked-field' : ''}`}
                  onClick={() => {
                    if (!isFieldsLocked) {
                      openPicker('proishozdenie', 24);
                    }
                  }}
                  style={{
                    ...wrappingCellStyle,
                    cursor: isFieldsLocked ? 'not-allowed' : 'pointer',
                    background: isFieldsLocked ? '#f8f9fa' : 'transparent'
                  }}
                >
                  <textarea
                    ref={proishozdenieTextareaRef}
                    readOnly
                    value={(() => {
                      const handbook = handbooks.find(h => h.id === 24);
                      if (handbook && handbook.references && displayData.proishozdenie) {
                        const selectedOption = handbook.references.find(r => r.id.toString() === displayData.proishozdenie);
                        return selectedOption ? selectedOption.name : '';
                      }
                      return '';
                    })()}
                    placeholder="☰"
                    style={{
                      ...wrappingTextareaStyle,
                      color: displayData.proishozdenie ? '#495057' : '#6c757d',
                      fontStyle: displayData.proishozdenie ? 'normal' : 'italic'
                    }}
                    className="form-control multiline-textarea"
                  />
                </div>
              </td>

              {/* ОЗУ */}
              <td className="coordinate-cell" style={{ minWidth: '280px', width: '280px' }}>
                <div
                  className={`reference-cell ${isFieldsLocked ? 'locked-field' : ''}`}
                  onClick={() => {
                    if (!isFieldsLocked) {
                      openPicker('ozu', 1);
                    }
                  }}
                  style={{
                    ...wrappingCellStyle,
                    cursor: isFieldsLocked ? 'not-allowed' : 'pointer',
                    background: isFieldsLocked ? '#f8f9fa' : 'transparent'
                  }}
                >
                  <textarea
                    ref={ozuTextareaRef}
                    readOnly
                    value={(() => {
                      const handbook = handbooks.find(h => h.id === 1);
                      if (handbook && handbook.references && displayData.ozu) {
                        const selectedOption = handbook.references.find(r => r.id.toString() === displayData.ozu);
                        return selectedOption ? selectedOption.name : '';
                      }
                      return '';
                    })()}
                    placeholder="☰"
                    style={{
                      ...wrappingTextareaStyle,
                      color: displayData.ozu ? '#495057' : '#6c757d',
                      fontStyle: displayData.ozu ? 'normal' : 'italic'
                    }}
                    className="form-control multiline-textarea"
                  />
                </div>
              </td>

              {/* Право пользования */}
              <td className="coordinate-cell" style={{ minWidth: '220px', width: '220px' }}>
                <div
                  className={`reference-cell ${isFieldsLocked ? 'locked-field' : ''}`}
                  onClick={() => {
                    if (!isFieldsLocked) {
                      openPicker('arenda', 11);
                    }
                  }}
                  style={{
                    ...wrappingCellStyle,
                    cursor: isFieldsLocked ? 'not-allowed' : 'pointer',
                    background: isFieldsLocked ? '#f8f9fa' : 'transparent'
                  }}
                >
                  <textarea
                    ref={arendaTextareaRef}
                    readOnly
                    value={(() => {
                      const handbook = handbooks.find(h => h.id === 11);
                      if (handbook && handbook.references && displayData.arenda) {
                        const selectedOption = handbook.references.find(r => r.id.toString() === displayData.arenda);
                        return selectedOption ? selectedOption.name : '';
                      }
                      return '';
                    })()}
                    placeholder="☰"
                    style={{
                      ...wrappingTextareaStyle,
                      color: displayData.arenda ? '#495057' : '#6c757d',
                      fontStyle: displayData.arenda ? 'normal' : 'italic'
                    }}
                    className="form-control multiline-textarea"
                  />
                </div>
              </td>

              {/* Вид использования */}
              <td className="coordinate-cell" style={{ minWidth: '240px', width: '240px' }}>
                <div
                  className={`reference-cell ${isFieldsLocked ? 'locked-field' : ''}`}
                  onClick={() => {
                    if (!isFieldsLocked) {
                      openPicker('vidarenda', 4);
                    }
                  }}
                  style={{
                    ...wrappingCellStyle,
                    cursor: isFieldsLocked ? 'not-allowed' : 'pointer',
                    background: isFieldsLocked ? '#f8f9fa' : 'transparent'
                  }}
                >
                  <textarea
                    ref={vidarendaTextareaRef}
                    readOnly
                    value={(() => {
                      const handbook = handbooks.find(h => h.id === 4);
                      if (handbook && handbook.references && displayData.vidarenda) {
                        const selectedOption = handbook.references.find(r => r.id.toString() === displayData.vidarenda);
                        return selectedOption ? selectedOption.name : '';
                      }
                      return '';
                    })()}
                    placeholder="☰"
                    style={{
                      ...wrappingTextareaStyle,
                      color: displayData.vidarenda ? '#495057' : '#6c757d',
                      fontStyle: displayData.vidarenda ? 'normal' : 'italic'
                    }}
                    className="form-control multiline-textarea"
                  />
                </div>
              </td>

              {/* ООПТ */}
              <td className="coordinate-cell" style={{ minWidth: '260px', width: '260px' }}>
                <div
                  className={`reference-cell ${isFieldsLocked ? 'locked-field' : ''}`}
                  onClick={() => {
                    if (!isFieldsLocked) {
                      openPicker('oopt', 3);
                    }
                  }}
                  style={{
                    ...wrappingCellStyle,
                    cursor: isFieldsLocked ? 'not-allowed' : 'pointer',
                    background: isFieldsLocked ? '#f8f9fa' : 'transparent'
                  }}
                >
                  <textarea
                    ref={ooptTextareaRef}
                    readOnly
                    value={(() => {
                      const handbook = handbooks.find(h => h.id === 3);
                      if (handbook && handbook.references && displayData.oopt) {
                        const selectedOption = handbook.references.find(r => r.id.toString() === displayData.oopt);
                        return selectedOption ? selectedOption.name : '';
                      }
                      return '';
                    })()}
                    placeholder="☰"
                    style={{
                      ...wrappingTextareaStyle,
                      color: displayData.oopt ? '#495057' : '#6c757d',
                      fontStyle: displayData.oopt ? 'normal' : 'italic'
                    }}
                    className="form-control multiline-textarea"
                  />
                </div>
              </td>

              {/* Иное ООПТ */}
              <td className="coordinate-cell" style={{ minWidth: '200px', width: '200px' }}>
                <div style={wrappingCellStyle}>
                  <textarea
                    ref={nooptTextareaRef}
                    value={displayData.noopt || ''}
                    onChange={(e) => {
                      if (!isFieldsLocked) {
                        const newData = { ...displayData, noopt: e.target.value };
                        setLocalFormData(newData);
                        if (setFormData) {
                          setFormData(newData);
                        }
                      }
                    }}
                    readOnly={isFieldsLocked}
                    className={`form-control multiline-textarea ${isFieldsLocked ? 'locked-field' : ''}`}
                    style={{
                      ...wrappingTextareaStyle,
                      cursor: isFieldsLocked ? 'not-allowed' : 'text',
                      background: isFieldsLocked ? '#f8f9fa' : 'transparent'
                    }}
                    placeholder="Введите текст"
                  />
                </div>
              </td>

              {/* Год лесоустройства */}
              <td className="coordinate-cell" style={{ minWidth: '120px', width: '120px' }}>
                <div 
                  className={`reference-cell ${isFieldsLocked ? 'locked-field' : ''}`}
                  onClick={() => {
                    if (!isFieldsLocked) {
                      openYearPicker();
                    }
                  }}
                  style={{
                    minHeight: 60,
                    height: '100%',
                    cursor: isFieldsLocked ? 'not-allowed' : 'pointer',
                    background: isFieldsLocked ? '#f8f9fa' : 'transparent',
                    position: 'relative'
                  }}
                >
                  <textarea
                    readOnly
                    value={displayData.glu || ''}
                    placeholder="☰"
                    style={{
                      border: 'none',
                      outline: 'none',
                      resize: 'none',
                      width: '100%',
                      height: '100%',
                      background: 'transparent',
                      color: displayData.glu ? '#495057' : '#6c757d',
                      fontStyle: displayData.glu ? 'normal' : 'italic'
                    }}
                    className={`form-control coordinate-field ${!displayData.glu ? 'required-field' : ''}`}
                  />
                </div>
              </td>

              {/* Информация пользователя */}
              <td className="coordinate-cell" style={{ minWidth: '150px', width: '150px' }}>
                <input
                  type="text"
                  value={displayData.itaks2 || ''}
                  onChange={(e) => {
                    if (!isFieldsLocked) {
                      const newData = { ...displayData, itaks2: e.target.value };
                      setLocalFormData(newData);
                      if (setFormData) {
                        setFormData(newData);
                      }
                    }
                  }}
                  readOnly={isFieldsLocked}
                  className={`form-control coordinate-field ${isFieldsLocked ? 'locked-field' : ''}`}
                  style={isFieldsLocked ? { background: '#f8f9fa', cursor: 'not-allowed' } : {}}
                  placeholder="Введите информацию"
                />
              </td>

              {/* Доступность участка: отмечено — доступен (true), снято — недоступен (false); до первого изменения — null */}
              <td
                className="coordinate-cell"
                style={{
                  minWidth: '150px',
                  width: '150px',
                  padding: '0',
                  textAlign: 'center',
                  verticalAlign: 'middle'
                }}
              >
                <select
                  value={
                    displayData.landAvailability === true
                      ? 'yes'
                      : displayData.landAvailability === false
                        ? 'no'
                        : ''
                  }
                  onChange={(e) => {
                    if (isFieldsLocked) return;
                    const v = e.target.value;
                    const newValue = v === 'yes' ? true : v === 'no' ? false : null;
                    const newData = { ...displayData, landAvailability: newValue };
                    setLocalFormData(newData);
                    if (setFormData) {
                      setFormData(newData);
                    }
                  }}
                  disabled={isFieldsLocked}
                  title="Укажите, доступен ли участок"
                  className={isFieldsLocked ? 'locked-field' : ''}
                  style={{
                    width: '100%',
                    height: '100%',
                    minHeight: '40px',
                    padding: '8px 12px',
                    border: 'none',
                    borderRadius: '0',
                    fontSize: '14px',
                    boxSizing: 'border-box',
                    cursor: isFieldsLocked ? 'not-allowed' : 'pointer',
                    backgroundColor: '#f8f9fa',
                    color: '#212529',
                    outline: 'none'
                  }}
                >
                  <option value="">Не выбрано</option>
                  <option value="yes">Да</option>
                  <option value="no">Нет</option>
                </select>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      
      {/* Слайдер для прокрутки таблицы информации */}
      <div style={{ 
        marginTop: '10px',
        padding: '10px',
        backgroundColor: '#f8f9fa',
        border: '1px solid #e9ecef',
        borderRadius: '6px',
        display: 'flex',
        alignItems: 'center',
        gap: '10px'
      }}>
        <span style={{ 
          fontSize: '12px', 
          color: '#6c757d',
          minWidth: '60px'
        }}>
          Прокрутка:
        </span>
        <input
          type="range"
          min="0"
          max="100"
          value={infoTableScrollLeft}
          onChange={(e) => {
            const scrollPercent = parseInt(e.target.value);
            if (infoTableScrollRef.current) {
              const maxScroll = infoTableScrollRef.current.scrollWidth - infoTableScrollRef.current.clientWidth;
              const scrollLeft = (scrollPercent / 100) * maxScroll;
              infoTableScrollRef.current.scrollLeft = scrollLeft;
            }
          }}
          className="table-scroll-slider"
          style={{
            flex: 1
          }}
        />
        <span style={{ 
          fontSize: '12px', 
          color: '#6c757d',
          minWidth: '30px',
          textAlign: 'right'
        }}>
          {Math.round(infoTableScrollLeft)}%
        </span>
      </div>
    </div>
  );
};

export default InfoSection;

